import { Injectable, QueryList } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { SurveyQuestionComponent } from '../survey-question/survey-question.component';

export interface Survey<T extends 'consent' | 'email' | 'writer' | 'file' = any> {
    id: string;
    type: T;
    answers: T extends 'consent'
    ? ConsentAnswers
    : T extends 'email'
    ? EmailAnswers
    : T extends 'writer'
    ? WriterAnswers
    : T extends 'file'
    ? FileAnswers
    : never;
    complete: boolean;
    /**
     * This survey is about this file
     */
    describesFile?: File;
    /**
     * These files contain the results;
     * or false if it should not be in the output
     */
    resultFiles: false | File[];
}

export interface EmailAnswers {
    contactEmail: string;
}
export interface ConsentAnswers {
    consentGiven: boolean;
}

export interface WriterAnswers {
    birthMonth: string;
    birthDateApproximate: boolean;
    birthCountry: string;
    birthSex: string;
    ethnicity: string;
    firstLanguages: string;
    otherLanguages: string;
    genderSame?: boolean;
    genderDetails: string;
    marital: string;
    occupation: string;
    residencyCountry: string;
    deathCountry: string;
    deathMonth: string;
    deathDateApproximate: boolean;
    deathMethod: string;
    deathDetails: string;
}

export interface FileAnswers {
    beforeDeath: string;
    audience: string[];
    country: string;
    language: string;
    format: string;
    formatDetails: string;
    furtherInformation: string;
}

type SurveyQuestionTexts = {
    seen: Set<string>,
    questions: { name: string, text: string }[]
}

@Injectable({
    providedIn: 'root'
})
export class SurveyService {
    private surveys = new BehaviorSubject<Survey[]>([]);

    /**
     * Files containing the answers for the completed surveys
     */
    private surveyFiles = new BehaviorSubject<File[]>([]);

    /**
     * Maps the survey ID and question names to the question text as shown
     */
    private questionTexts: {
        [surveyId: string]: SurveyQuestionTexts
    } = {};

    /**
     * Surveys which have to be filled in
     */
    incompleteSurveys$ = this.surveys.pipe(
        map(surveys => surveys.filter(s => !s.complete)));


    /**
     * Keep the email address separately, to identify unencrypted submissions
     */
    email: string = '';
    surveyFiles$ = this.surveyFiles.asObservable();

    constructor() { }

    addSurveys(surveys: Survey[]) {
        this.uniqueSurveyIds(surveys);
        this.surveys.next([
            ...this.surveys.value,
            ...surveys
        ]);
    }

    /**
     * Deletes a survey and its associated files
     */
    deleteSurvey(survey: Survey) {
        const resultFiles = survey.resultFiles;
        if (resultFiles) {
            this.surveyFiles.next(
                this.surveyFiles.value.filter(f => resultFiles.indexOf(f) === -1));
        }
        this.surveys.next(this.surveys.value.filter(s => s !== survey));
    }

    /** Deletes all the surveys which have to do with this file */
    deleteFileSurveys(file: File) {
        for (const survey of this.surveys.value.filter(
            s => s.describesFile === file ||
                s.resultFiles && s.resultFiles.indexOf(file) !== -1)) {
            this.deleteSurvey(survey);
        }
    }

    reopenSurvey(index: number) {
        const surveys = this.surveys.value;
        surveys[index].complete = false;
        this.surveys.next([...surveys]);
    }

    /**
     * Starts the survey process if needed
     * @returns true if the entry was started
     */
    entry() {
        if (this.surveys.value.length == 0) {
            this.addSurveys([
                // always start with this survey
                {
                    id: 'consent',
                    type: 'consent',
                    answers: <ConsentAnswers>{},
                    complete: false,
                    resultFiles: false,
                },{
                    id: 'email',
                    type: 'email',
                    answers: <EmailAnswers>{},
                    complete: false,
                    resultFiles: false
                }]);

            return true;
        }

        return false;
    }

    complete(surveyId: string, answers: Survey['answers']) {
        const surveys = this.surveys.value;
        const index = surveys.findIndex(survey => survey.id === surveyId);
        const survey = {
            ...surveys[index],
            complete: true,
            answers
        };
        if (survey.resultFiles) {
            survey.resultFiles = this.surveyToFiles(surveys[index]);
            this.surveyFiles.next([...this.surveyFiles.value, ...survey.resultFiles])
        }
        surveys[index] = survey;
        this.surveys.next([...surveys]);
    }

    registerQuestions(survey: Survey<any>, questions: QueryList<SurveyQuestionComponent<any>>) {
        let surveyQuestions: SurveyQuestionTexts;
        if (this.questionTexts[survey.id]) {
            surveyQuestions = this.questionTexts[survey.id];
        } else {
            this.questionTexts[survey.id] = surveyQuestions = {
                seen: new Set<string>(),
                questions: []
            };
        }

        for (const question of questions) {
            if (!surveyQuestions.seen.has(question.name)) {
                surveyQuestions.seen.add(question.name);
                surveyQuestions.questions.push({
                    name: question.name,
                    text: question.question ?? question.name
                })
            }
        }
    }

    private surveyToFiles(survey: Survey): File[] {
        const jsonFile = new File(
            [new Blob([this.surveyToJson(survey)], { type: 'text/json' })],
            survey.id + '.json');

        const txtFile = new File(
            [new Blob([this.surveyToTxt(survey)], { type: 'text/plain' })],
            survey.id + '.txt');

        return [jsonFile, txtFile];
    }

    private surveyToJson(survey: Survey): string {
        return JSON.stringify(survey.answers);
    }

    private surveyToTxt(survey: Survey): string {
        const surveyQuestions = this.questionTexts[survey.id];
        let output: string[] = [];

        for (const question of surveyQuestions.questions) {
            const answer = (<any>survey.answers)[question.name];
            const answerOutput = this.surveyQuestionToText(question.text, answer);
            if (answerOutput !== undefined) {
                output.push(answerOutput);
            }
        }
        return output.join('\n\n');
    }

    private surveyQuestionToText(questionText: string, answer: any): string | undefined {
        // skip empty answers
        if (answer === undefined) {
            return undefined;
        }

        // this is also an empty answers
        if (typeof (answer) === 'string') {
            if (answer.trim() === '') {
                return undefined;
            }
        }

        // a negative boolean is not an empty answer
        if (typeof (answer) === 'boolean') {
            answer = answer ? $localize`YES` : $localize`NO`;
        }

        if (Array.isArray(answer)) {
            if (answer.length > 1) {
                // put them all on a separate line
                answer = answer.map(a => `- ${a}`).join('\n\t'); // with a tab because the first answer is also with a tab
            }
        }


        return `${questionText}

\t${answer}`;
    }

    /**
     * Updates the survey IDs in place if needed to ensure that they are unique.
     */
    private uniqueSurveyIds(surveys: Survey[]): void {
        const surveyIds = new Set(this.surveys.value.map(s => s.id));

        for (const survey of surveys) {
            let postfix = '';
            let i = 0;
            while (surveyIds.has(survey.id + postfix)) {
                postfix = `_${i}`;
                i++;
            }
            if (postfix !== '') {
                survey.id += postfix;
            }
            surveyIds.add(survey.id);
        }
    }

}
